<template>
  <div>
    <q-card
      class="q-pa-none row"
    >
      <q-card-section
        horizontal
        class="row col-12 wrap-card"
      >
        <q-carousel
          ref="carousel"
          v-model="slide"
          arrows
          swipeable
          control-color="semitransparent"
          control-text-color="grey-9"
          control-type="unelevated"
          transition-prev="slide-right"
          transition-next="slide-left"
          animated
          class="col-xs-12 col-md-4 control-buttons carousel"
        >
          <q-carousel-slide
            v-for="(sessionProgram, sessionProgramIndex) in carouselSessionPrograms"
            :key="sessionProgram.id"
            v-ripple
            :name="sessionProgram.id"
            :img-src="sessionProgram.program.coverphoto"
            class="cursor-pointer"
            :class="getSlideBgColorClass(sessionProgram, sessionProgramIndex)"
            @click="onShowProgramInfo(sessionProgram.program)"
          >
            <div class="absolute-bottom custom-caption">
              <div class="text-subtitle1 ellipsis">
                <q-icon
                  v-if="sessionProgram.program.status === 'closed'"
                  name="emoji_events"
                  size="md"
                  color="yellow"
                />{{ sessionProgram.programName }}
              </div>
            </div>
          </q-carousel-slide>
        </q-carousel>
        <q-card-section
          v-ripple="canAddManualSessionEntry"
          class="col-xs-12 col-md-8 q-ma-none q-pa-none column"
          :class="{ 'cursor-pointer': canAddManualSessionEntry }"
          @click="onPrimaryAction"
        >
          <q-card-section class="row col items-top full-width">
            <div class="col">
              <div
                class="text-subtitle2 ellipsis"
              >
                <span>{{ sessionNameFormatted }}</span>
              </div>
            </div>
            <q-space />
            <div class="col-auto">
              <q-btn
                v-if="hasNotificationSettings"
                size="sm"
                flat
                round
                :icon="sessionNotificationIcon"
                :disable="offlineMode"
                color="dark"
                @click.stop
              >
                <q-menu
                  @hide="onCloseCustomNotificationMenu"
                >
                  <div
                    class="q-pa-xs text-center text-subtitle2 bg-primary text-white"
                  >
                    {{ $t('sessions.sessionCard.textTitleNotifications') }}
                  </div>
                  <q-separator />
                  <q-list>
                    <q-item
                      v-close-popup
                      :active="notificationSettings.type === 'all'"
                      clickable
                      :disable="usingFakeRole"
                      @click="onUpdateNotifications({type: 'all', custom: []})"
                    >
                      <q-item-section avatar>
                        <q-icon name="notifications_active" />
                      </q-item-section>
                      <q-item-section>
                        {{ $t('sessions.sessionCard.textNotificationsOn') }}
                      </q-item-section>
                    </q-item>
                    <q-item
                      :active="notificationSettings.type === 'custom'"
                      clickable
                    >
                      <q-item-section avatar>
                        <q-icon name="notifications_none" />
                      </q-item-section>
                      <q-item-section>
                        {{ $t('sessions.sessionCard.textNotificationsCustom') }}
                      </q-item-section>
                      <q-item-section side>
                        <q-icon name="keyboard_arrow_right" />
                      </q-item-section>
                      <q-menu>
                        <q-item
                          v-for="notificationEventType in customNotificationEventTypes"
                          :key="notificationEventType.value"
                          :disable="usingFakeRole"
                        >
                          <q-item-section>
                            <q-toggle
                              v-model="notificationSettings.custom"
                              :label="notificationEventType.label"
                              :val="notificationEventType.value"
                              :disable="usingFakeRole"
                              checked-icon="notifications_none"
                              color="green"
                              unchecked-icon="notifications_off"

                              @update:model-value="onUpdateNotifications({type: 'custom'})"
                            />
                          </q-item-section>
                        </q-item>
                        <q-separator />
                        <q-item>
                          <q-item-section>
                            <q-toggle
                              v-model="notificationSettings.notifyImmediately"
                              :label="$t('sessions.sessionCard.labelNotifyImmediately')"
                              :val="true"
                              :disable="usingFakeRole"
                              checked-icon="flash_on"
                              color="green"
                              unchecked-icon="watch_later"

                              @update:model-value="onUpdateNotifications({type: 'custom'})"
                            >
                              <q-tooltip>
                                {{ $t('sessions.sessionCard.textNotifyImmediatelyHint') }}
                              </q-tooltip>
                            </q-toggle>
                          </q-item-section>
                        </q-item>
                      </q-menu>
                    </q-item>
                    <q-item
                      v-close-popup
                      :active="notificationSettings.type === 'none'"
                      clickable
                      :disable="usingFakeRole"
                      @click="onUpdateNotifications({type: 'none', custom: []})"
                    >
                      <q-item-section avatar>
                        <q-icon name="notifications_off" />
                      </q-item-section>
                      <q-item-section>
                        {{ $t('sessions.sessionCard.textNotificationsOff') }}
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-menu>
                <q-tooltip>
                  {{ $t('sessions.sessionCard.textTitleNotifications') }}
                </q-tooltip>
              </q-btn>
              <q-btn
                v-if="userCanSeeMenu && !sessionIsRunning"
                size="sm"
                flat
                round
                icon="more_vert"
                :disable="offlineMode"
                @click.stop
              >
                <q-menu
                  v-model="showMenu"
                  @hide="onCloseVisibilityMenu"
                >
                  <q-list style="min-width: 100px">
                    <q-item
                      v-if="userCanEditScheduledSession"
                      v-close-popup
                      :clickable="!sessionIsRunning"
                      :disable="sessionIsRunning || usingFakeRole"
                      @click="onEditSession"
                    >
                      <q-item-section
                        avatar
                      >
                        <q-icon
                          name="edit"
                        />
                      </q-item-section>
                      <q-item-section>
                        {{ $t('sessions.sessionCard.textEditSession') }}
                      </q-item-section>
                    </q-item>
                    <q-item
                      v-if="userCanEditSessionVisibility"
                      :clickable="!sessionIsRunning"
                      :disable="sessionIsRunning"
                    >
                      <q-item-section avatar>
                        <q-icon name="visibility" />
                      </q-item-section>
                      <q-item-section>
                        {{ $t('sessions.sessionCard.textVisibility') }}
                      </q-item-section>
                      <q-item-section side>
                        <q-icon name="keyboard_arrow_right" />
                      </q-item-section>
                      <q-menu>
                        <q-item>
                          <q-item-section>
                            <q-toggle
                              :model-value="visibilitySettings.limitedVisibility"
                              :label="$t('sessions.sessionCard.toggleLimitVisibilityLabel')"
                              checked-icon="visibility"
                              :val="visibilitySettings.value"
                              :disable="usingFakeRole"
                              color="green"
                              unchecked-icon="visibility_off"
                              @update:model-value="onUpdateVisibilitySetting({ limitedVisibility: $event }, true)"
                            />
                          </q-item-section>
                        </q-item>
                        <template
                          v-if="visibilitySettings.limitedVisibility"
                        >
                          <q-separator />
                          <q-item
                            v-for="visibilityOption in visibilityOptions"
                            :key="visibilityOption.value"
                            :disable="usingFakeRole || visibilityOption.disable"
                            tag="label"
                          >
                            <q-item-section
                              class="col-auto"
                            >
                              <q-toggle
                                :model-value="visibilitySettings.visibility"
                                :val="visibilityOption.value"
                                :disable="usingFakeRole || visibilityOption.disable"
                                checked-icon="visibility"
                                color="green"
                                unchecked-icon="visibility_off"
                                @update:model-value="onToggleVisibility(visibilityOption.value)"
                              />
                            </q-item-section>
                            <q-item-section class="col">
                              <q-item-label>
                                {{ visibilityOption.label }}
                              </q-item-label>
                              <q-item-label caption>
                                {{ visibilityOption.hint }}
                              </q-item-label>
                            </q-item-section>
                          </q-item>
                          <q-separator />
                          <q-item>
                            <q-item-section>
                              <q-toggle
                                :model-value="visibilitySettings.limitHistoryVisibility"
                                :label="$t('sessions.sessionCard.toggleLimitHistoryVisibilityLabel')"
                                :true-value="false"
                                :false-value="true"
                                :disable="usingFakeRole"
                                checked-icon="visibility"
                                unchecked-icon="visibility_off"
                                color="green"
                                @update:model-value="onUpdateVisibilitySetting({limitHistoryVisibility: $event})"
                              />
                            </q-item-section>
                          </q-item>
                        </template>
                      </q-menu>
                    </q-item>
                    <q-item
                      v-if="userCanDeleteScheduledSession"
                      v-close-popup
                      clickable
                      :disable="usingFakeRole"
                      @click="onDeleteSession"
                    >
                      <q-item-section
                        avatar
                        class="text-no-wrap"
                      >
                        <q-icon
                          name="delete"
                        />
                      </q-item-section>
                      <q-item-section class="text-no-wrap">
                        {{ $t('sessions.sessionCard.textDeleteSession') }}
                      </q-item-section>
                    </q-item>
                    <q-item
                      v-close-popup
                      clickable
                      @click="onCopySupportInfo()"
                    >
                      <q-item-section
                        avatar
                      >
                        <q-icon
                          name="content_copy"
                        />
                      </q-item-section>
                      <q-item-section class="text-no-wrap">
                        {{ $t('common.copySupportInfo') }}
                      </q-item-section>
                    </q-item>
                  </q-list>
                </q-menu>
              </q-btn>
            </div>
          </q-card-section>
          <q-card-section
            class="q-pb-none row justify-end items-center"
          >
            <div class="col-xs-12 col-sm">
              <span
                v-for="(weekday, weekdayIndex) in weekdays"
                :key="weekdayIndex"
              >
                <transition
                  v-if="weekdayHasSession(weekdayIndex)"
                  appear
                  enter-active-class="animated bounceIn slower delay-2s"
                >
                  <q-chip
                    :size="weekdaySize(weekdayIndex)"
                    :class="weekdayClass(weekdayIndex)"
                    square
                  >
                    {{ weekday }}
                  </q-chip>
                </transition>
                <q-chip
                  v-else
                  :size="weekdaySize(weekdayIndex)"
                  :class="weekdayClass(weekdayIndex)"
                  square
                >
                  {{ weekday }}
                </q-chip>
              </span>
            </div>
            <q-space />
            <div
              v-if="canAddManualSessionEntry"
              class="q-ml-md q-my-md"
            >
              <q-btn
                flat
                :label="$t('sessions.sessionCard.buttonAddActivity')"
                icon="slideshow"
                color="positive"
              />
            </div>
          </q-card-section>
        </q-card-section>
      </q-card-section>
    </q-card>
  </div>
</template>
<script>

import SessionUploadWarning from 'components/Modals/SessionUploadWarning';
import ProgramInfo from 'components/Programs/ProgramInfo';
import AddActivity from 'components/Sessions/Modals/AddActivity';
import {
  chain, find, get, isEmpty, size,
} from 'lodash';
import { mapState } from 'pinia';
import { useAuth } from 'src/composables/auth';
import { ACTIVE_PROGRAM_STATUSES } from 'src/constants/programs';
import SessionProgramsDB from 'src/fb/sessionPrograms-db';
import Interactions from 'src/mixins/Interactions';
import SessionCardsCommon from 'src/mixins/SessionCardsCommon';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';
import { firebaseUploader } from 'src/store/firebaseUploader';
import { network } from 'src/store/network';
import { programs } from 'src/store/programs';

export default {
  name: 'ParentSessionCard',

  mixins: [
    Interactions,
    SessionCardsCommon,
    Toolbox,
  ],

  props: {
    usingFakeRole: {
      type: Boolean,
      required: true,
    },
  },

  setup() {
    const { userHasPrivilege } = useAuth();

    return { userHasPrivilege };
  },

  data() {
    return {
      slideColors: ['red', 'pink', 'purple', 'deep-purple', 'indigo', 'blue', 'light-blue',
        'cyan', 'teal', 'green', 'light-green', 'lime', 'yellow', 'amber', 'orange',
        'deep-orange', 'brown'],

      slide: '',

      showMenu: false,

      sessionPrograms: [],

      customNotificationEventTypes: [
        {
          value: 'sessionFinished',
          label: this.$t('activityFeed.events.sessionFinished'),
        },
        // @todo: HR-427
        /* {
          value: 'sessionNotesAdded',
          label: this.$t('activityFeed.events.sessionNotesAdded'),
        }, */
        {
          value: 'sessionFilesAttached',
          label: this.$t('activityFeed.events.filesAttached'),
        },
      ],

    };
  },

  computed: {
    ...mapState(auth, ['userDetails']),
    ...mapState(programs, ['programs']),
    ...mapState(firebaseUploader, ['uploadQueue']),
    ...mapState(network, ['offlineMode']),

    carouselSessionPrograms() {
      return chain(this.sessionPrograms)
        .map((sessionProgram) => ({
          ...sessionProgram,
          program: find(this.programs, { id: sessionProgram.program }),
        }))
        .reject((sessionProgram) => isEmpty(sessionProgram.program)
            || (sessionProgram.hidden)
            || (![...ACTIVE_PROGRAM_STATUSES, 'closed'].includes(sessionProgram.program.status)))
        .value();
    }, // carouselSessionPrograms()

    lastSessionProgram() {
      return chain(this.carouselSessionPrograms)
        .find({ program: { status: 'active' } })
        .get('id')
        .value();
    }, // lastSessionProgram()

    lastSessionProgramName() {
      return chain(this.carouselSessionPrograms)
        .find({ program: { status: 'active' } })
        .get('programName')
        .value();
    }, // lastSessionProgram()

    canAddManualSessionEntry() {
      return !this.usingFakeRole && this.userHasPrivilege('sessions/enterManualSessions');
    }, // canAddManualSessionEntry()
  },

  watch: {
    session: {
      deep: true,
      immediate: true,
      async handler(session, oldSession) {
        if (!get(session, 'id', false)) {
          return;
        }
        if (get(session, 'id') !== get(oldSession, 'id')) {
          this.loadNotificationSettings(session.id);
        }
        this.setVisibilitySettings(session);
        await this.loadSessionPrograms(session.id);
      },
    }, // session

    carouselSessionPrograms: {
      deep: true,
      immediate: false,
      handler() {
        // Select current program
        if (this.slide !== this.lastSessionProgram) {
          this.slide = this.lastSessionProgram;
        }
      },
    }, // carouselSessionPrograms

  },

  methods: {

    onShowProgramInfo(program) {
      if (this.usingFakeRole) {
        return;
      }

      this.openComponentDialog(ProgramInfo, { program });
    }, // onShowProgram()

    onPrimaryAction() {
      if (!this.canAddManualSessionEntry) {
        return;
      }

      this.onAddActivity();
    }, // onPrimaryAction()

    onAddActivity() {
      this.openComponentDialog(AddActivity, {
        session: this.session,
        sessionName: this.lastSessionProgramName,
      }, () => {
        // Done...
        if (size(this.uploadQueue) > 0) {
          this.openComponentDialog(SessionUploadWarning, {
            preventClosing: true,
          });
        }
      });
    }, // onAddActivity()

    async loadSessionPrograms(sessionId) {
      const sessionProgramsDb = new SessionProgramsDB(sessionId);
      this.sessionPrograms = await sessionProgramsDb.readOrdered('order');
    }, // loadSessionPrograms()

    getSlideBgColorClass(sessionProgram, index) {
      if (get(sessionProgram, ['program', 'coverphoto'])) {
        return '';
      }
      return `bg-${get(this.slideColors, index % this.slideColors.length, '')}`;
    }, // getSlideColor()

  },
};
</script>
<style lang="scss" scoped>
.custom-caption {
  text-align: center;
  padding: 12px;
  color: white;
  background-color: rgba(0, 0, 0, .3);
}

.wrap-card {
  flex-wrap: wrap !important;
}

.carousel {
  min-height: 96px
}

.control-buttons {
  background-color: rgba(0, 0, 0, .3);
}
</style>
